import getFirebase from './firebase';
import { DEFAULT_MASTER_CONFIG, DEFAULT_MIGRATION_TRACKING } from '../config/firebaseConfig';

const firebase = getFirebase();
const db = firebase.firestore();

class ConfigService {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  async getMasterConfig() {
    const cacheKey = 'masterConfig';

    // Check cache first
    if (this.isValidCache(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const docRef = db.collection('master').doc('config');
      const docSnap = await docRef.get();

      let config;
      if (docSnap.exists) {
        config = docSnap.data();
      } else {
        // Initialize with defaults if doesn't exist
        config = DEFAULT_MASTER_CONFIG;
        await docRef.set(config);
      }

      // Cache the result
      this.setCache(cacheKey, config);
      return config;
    } catch (error) {
      console.error('Error fetching master config:', error);
      return DEFAULT_MASTER_CONFIG;
    }
  }

  async updateMasterConfig(updates) {
    try {
      const docRef = db.collection('master').doc('config');
      const updateData = {
        ...updates,
        lastUpdated: new Date().toISOString()
      };

      await docRef.update(updateData);

      // Invalidate cache
      this.invalidateCache('masterConfig');

      return true;
    } catch (error) {
      console.error('Error updating master config:', error);
      throw error;
    }
  }

  async getCurrentActiveYear() {
    const config = await this.getMasterConfig();
    return config.currentActiveYear;
  }

  async getMigrationFlags() {
    const config = await this.getMasterConfig();
    return config.migrationFlags;
  }

  async updateMigrationFlag(flagName, value) {
    return this.updateMasterConfig({
      [`migrationFlags.${flagName}`]: value
    });
  }

  async getMigrationTracking() {
    const cacheKey = 'migrationTracking';

    if (this.isValidCache(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const docRef = db.collection('master').doc('migrationTracking');
      const docSnap = await docRef.get();

      let tracking;
      if (docSnap.exists) {
        tracking = docSnap.data();
      } else {
        tracking = DEFAULT_MIGRATION_TRACKING;
        await docRef.set(tracking);
      }

      this.setCache(cacheKey, tracking);
      return tracking;
    } catch (error) {
      console.error('Error fetching migration tracking:', error);
      return DEFAULT_MIGRATION_TRACKING;
    }
  }

  async updateMigrationTracking(updates) {
    try {
      const docRef = db.collection('master').doc('migrationTracking');
      await docRef.update(updates);

      this.invalidateCache('migrationTracking');
      return true;
    } catch (error) {
      console.error('Error updating migration tracking:', error);
      throw error;
    }
  }

  // Cache management
  isValidCache(key) {
    const cached = this.cache.get(key);
    const expiry = this.cacheExpiry.get(key);
    return cached && expiry && Date.now() < expiry;
  }

  setCache(key, value) {
    this.cache.set(key, value);
    this.cacheExpiry.set(key, Date.now() + this.CACHE_DURATION);
  }

  invalidateCache(key) {
    this.cache.delete(key);
    this.cacheExpiry.delete(key);
  }

  clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }
}

export const configService = new ConfigService();
